import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { RECAPTCHA_SITE_KEY } from '../config/recaptcha';

export type RecaptchaState = 'loading' | 'ready' | 'blocked';

/**
 * Thrown by useRecaptchaToken when no token can be issued. The caller must
 * NOT send the auth request without a token - the backend now rejects it
 * with 422 unconditionally.
 */
export class RecaptchaError extends Error {
  reason: 'unavailable' | 'execute_failed';

  constructor(reason: 'unavailable' | 'execute_failed') {
    super(`reCAPTCHA token unavailable: ${reason}`);
    this.reason = reason;
  }
}

// Default id of the <script> tag injected by react-google-recaptcha-v3.
const SCRIPT_ID = 'google-recaptcha-v3';
// Blockers usually make the request fail silently, so a timeout is the main
// signal that the script never loads.
const LOAD_TIMEOUT_MS = 6000;

interface RecaptchaBlockedContextValue {
  blocked: boolean;
  /** Re-triggers the script load by remounting GoogleReCaptchaProvider. */
  retry: () => void;
}

const RecaptchaBlockedContext = createContext<RecaptchaBlockedContextValue>({
  blocked: false,
  retry: () => {},
});

/**
 * Rendered inside GoogleReCaptchaProvider. Reports "blocked" when the script
 * does not become available within LOAD_TIMEOUT_MS after mount, or when its
 * <script> tag fires `error`. The library injects the tag after this effect
 * runs, so the error listener sits on `document` in the capture phase
 * (`error` does not bubble).
 */
const RecaptchaLoadWatcher: React.FC<{ onBlocked: () => void }> = ({ onBlocked }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    if (executeRecaptcha) {
      return;
    }
    const timer = window.setTimeout(onBlocked, LOAD_TIMEOUT_MS);
    const onScriptError = (e: Event) => {
      if ((e.target as HTMLElement | null)?.id === SCRIPT_ID) {
        onBlocked();
      }
    };
    document.addEventListener('error', onScriptError, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('error', onScriptError, true);
    };
  }, [executeRecaptcha, onBlocked]);

  return null;
};

/**
 * Shared reCAPTCHA v3 wrapper: renders children inside GoogleReCaptchaProvider
 * when a site key is configured, or as-is when reCAPTCHA is disabled (dev).
 * Also owns the blocked-script detection so every captcha-protected form
 * reads the same state - do not duplicate timers in pages.
 */
export const RecaptchaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [blocked, setBlocked] = useState(false);
  // Bumping the key remounts GoogleReCaptchaProvider: its cleanup removes the
  // old <script> tag, the fresh mount injects a new one (verified against
  // react-google-recaptcha-v3 v1.11.0 internals).
  const [attempt, setAttempt] = useState(0);

  const onBlocked = useCallback(() => setBlocked(true), []);
  const retry = useCallback(() => {
    setBlocked(false);
    setAttempt((a) => a + 1);
  }, []);
  const contextValue = useMemo(() => ({ blocked, retry }), [blocked, retry]);

  if (!RECAPTCHA_SITE_KEY) {
    return <>{children}</>;
  }
  return (
    <RecaptchaBlockedContext.Provider value={contextValue}>
      <GoogleReCaptchaProvider key={attempt} reCaptchaKey={RECAPTCHA_SITE_KEY}>
        <RecaptchaLoadWatcher onBlocked={onBlocked} />
        {children}
      </GoogleReCaptchaProvider>
    </RecaptchaBlockedContext.Provider>
  );
};

/**
 * Tri-state of the reCAPTCHA script. `ready` when tokens can be issued (or
 * reCAPTCHA is disabled by an empty site key), `blocked` when the script
 * failed to load (ad blockers, proxies), `loading` otherwise.
 * Must be used inside RecaptchaProvider.
 */
export const useRecaptchaState = (): RecaptchaState => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { blocked } = useContext(RecaptchaBlockedContext);
  // Order matters: without a site key there is no GoogleReCaptchaProvider and
  // useGoogleReCaptcha returns the library's throwing default - never call it.
  if (!RECAPTCHA_SITE_KEY) {
    return 'ready';
  }
  if (executeRecaptcha) {
    return 'ready';
  }
  return blocked ? 'blocked' : 'loading';
};

/**
 * Returns a function that fetches a reCAPTCHA v3 token for the given action.
 * Resolves to null only when reCAPTCHA is disabled by an empty site key
 * (dev builds - the backend accepts a missing token only there). Otherwise a
 * missing script or a failed execute throws RecaptchaError: do not fall back
 * to sending the request without a token, it is guaranteed to 422.
 * Must be used inside RecaptchaProvider.
 */
export const useRecaptchaToken = (): ((action: string) => Promise<string | null>) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  return useCallback(async (action: string) => {
    if (!RECAPTCHA_SITE_KEY) {
      return null;
    }
    if (!executeRecaptcha) {
      throw new RecaptchaError('unavailable');
    }
    try {
      return await executeRecaptcha(action);
    } catch (err) {
      console.error('reCAPTCHA execution failed:', err);
      throw new RecaptchaError('execute_failed');
    }
  }, [executeRecaptcha]);
};

/**
 * Inline (non-toast) notice for captcha problems, shown above the submit
 * button. `blocked` explains how to unblock the script and offers a retry;
 * `failed` explains what to do after a 422 / failed token fetch.
 */
export const RecaptchaNotice: React.FC<{ variant: 'blocked' | 'failed' }> = ({ variant }) => {
  const { t } = useTranslation();
  const { retry } = useContext(RecaptchaBlockedContext);

  return (
    <div className="captcha-notice" role="alert">
      {variant === 'blocked' ? (
        <>
          <strong className="captcha-notice-title">{t('captcha_blocked_title')}</strong>
          <p className="captcha-notice-text">{t('captcha_blocked_help')}</p>
          <button type="button" className="captcha-notice-retry" onClick={retry}>
            {t('captcha_retry')}
          </button>
        </>
      ) : (
        <p className="captcha-notice-text">{t('captcha_failed_help')}</p>
      )}
    </div>
  );
};
